<template>
    <v-card
        class="prompt-input"
        :class="{ 'prompt-input-dragging': isDragging }"
        color="darkerbase"
        rounded="xl"
        elevation="0"
        @dragenter="handleDragEnter"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop">
        <v-form @submit.prevent="submit">
            <v-expand-transition>
                <div v-if="attachments.length" class="prompt-input-attachments">
                    <AttachmentList
                        :attachments="attachments"
                        :disabled="busy"
                        removable
                        @remove="$emit('remove-file', $event)" />
                </div>
            </v-expand-transition>

            <v-textarea
                class="prompt-input-field"
                :model-value="modelValue"
                :disabled="busy"
                placeholder="Work with GENERIO AGENT"
                aria-label="Describe the 3D model you would like to generate"
                variant="plain"
                rows="3"
                max-rows="6"
                auto-grow
                autofocus
                hide-details
                no-resize
                @update:model-value="$emit('update:modelValue', $event)"
                @keydown="handleKeydown" />

            <div class="prompt-input-actions">
                <input ref="files" class="file-input" type="file" accept="image/png,image/jpeg,image/webp,image/bmp,image/tiff" multiple :disabled="busy" tabindex="-1" @change="selectFiles" />
                <v-btn
                    class="prompt-input-add"
                    variant="text"
                    rounded="lg"
                    prepend-icon="fa-solid fa-plus"
                    aria-label="Add files"
                    :disabled="busy"
                    @click="$refs.files.click()">
                    <span class="d-none d-sm-inline">Add files</span>
                </v-btn>
                <v-btn
                    class="prompt-input-generate"
                    type="submit"
                    variant="flat"
                    rounded="lg"
                    color="color1"
                    append-icon="fa-solid fa-wand-magic-sparkles"
                    aria-label="Generate model"
                    :loading="busy"
                    :disabled="busy || !modelValue.trim()">
                    <span class="d-none d-sm-inline">Generate</span>
                </v-btn>
            </div>
        </v-form>
        <Transition name="agent-fade">
            <div v-if="isDragging" class="prompt-input-dropzone" aria-hidden="true">
                <v-icon icon="fa-regular fa-image" size="28" />
                <span>Drop reference images here</span>
            </div>
        </Transition>
    </v-card>
</template>

<script>
import AttachmentList from '../attachments/AttachmentList.vue'

export default {
    name: 'PromptInput',
    components: { AttachmentList },
    props: {
        modelValue: { type: String, default: '' },
        busy: { type: Boolean, default: false },
        attachments: { type: Array, default: () => [] }
    },
    emits: ['update:modelValue', 'add-files', 'remove-file', 'submit'],
    data() {
        return { dragDepth: 0 }
    },
    computed: {
        isDragging() { return !this.busy && this.dragDepth > 0 }
    },
    watch: {
        busy(value) { if (value) this.dragDepth = 0 }
    },
    methods: {
        isFileDrag(event) {
            return Array.from(event.dataTransfer?.types || []).includes('Files')
        },
        handleDragEnter(event) {
            if (!this.isFileDrag(event)) return
            event.preventDefault()
            event.stopPropagation()
            if (!this.busy) this.dragDepth++
        },
        handleDragOver(event) {
            if (!this.isFileDrag(event)) return
            event.preventDefault()
            event.stopPropagation()
            event.dataTransfer.dropEffect = this.busy ? 'none' : 'copy'
        },
        handleDragLeave(event) {
            if (!this.dragDepth) return
            event.preventDefault()
            event.stopPropagation()
            this.dragDepth = Math.max(0, this.dragDepth - 1)
        },
        handleDrop(event) {
            this.dragDepth = 0
            if (!this.isFileDrag(event) && !event.dataTransfer?.files?.length) return
            event.preventDefault()
            event.stopPropagation()
            if (this.busy) return
            const files = Array.from(event.dataTransfer.files || [])
            if (files.length) this.$emit('add-files', files)
        },
        submit() {
            if (!this.busy && this.modelValue.trim()) this.$emit('submit')
        },
        handleKeydown(event) {
            if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
            event.preventDefault()
            this.submit()
        },
        selectFiles(event) {
            if (this.busy) return
            this.$emit('add-files', Array.from(event.target.files))
            event.target.value = ''
        }
    }
}
</script>

<style scoped>
.prompt-input {
    width: 100%;
    border: 1px solid rgb(var(--v-theme-lightbase));
    padding: 20px;
    text-align: left;
    box-shadow: 0 12px 40px rgba(var(--v-theme-black), 0.06) !important;
    transition: border-color var(--agent-motion-duration, 300ms) ease,
        box-shadow var(--agent-motion-duration, 300ms) ease;
}

.prompt-input:focus-within {
    border-color: rgba(var(--v-theme-color1), 0.65);
    box-shadow: 0 12px 40px rgba(var(--v-theme-color1), 0.08) !important;
}

.prompt-input-dragging {
    border-color: rgb(var(--v-theme-color1));
}

.prompt-input-dropzone {
    position: absolute;
    inset: 8px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px;
    border: 2px dashed rgb(var(--v-theme-color1));
    border-radius: 16px;
    background: rgba(var(--v-theme-darkbase), 0.94);
    color: rgb(var(--v-theme-primary));
    font-size: 14px;
    text-align: center;
    pointer-events: none;
}

.prompt-input-dropzone .v-icon {
    color: rgb(var(--v-theme-color1));
}

.prompt-input-attachments {
    margin-bottom: 12px;
}

.prompt-input-field :deep(.v-field__input) {
    padding-top: 0;
    font-size: 16px;
    line-height: 1.6;
    mask-image: none;
}

.prompt-input-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}

.prompt-input-actions .v-btn {
    min-height: 42px;
    text-transform: none;
    letter-spacing: normal;
}

.prompt-input-add {
    color: rgb(var(--v-theme-secondary));
}

.prompt-input-generate {
    padding-inline: 20px;
    color: rgb(var(--v-theme-white)) !important;
}

.file-input {
    display: none;
}

@media (max-width: 599.98px) {
    .prompt-input {
        padding: 16px;
    }

    .prompt-input-actions .v-btn {
        width: 44px;
        min-width: 44px;
        height: 44px;
        padding: 0;
    }

    .prompt-input-actions :deep(.v-btn__prepend),
    .prompt-input-actions :deep(.v-btn__append) {
        margin: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .prompt-input {
        transition: none;
    }
}
</style>
